import type { LatLng, LatLngExpression, Map as LeafletMap } from "leaflet"
import L, { Point } from "leaflet"
import type { ReactElement, RefObject } from "react"
import { createRef, useEffect, useState } from "react"
import { useMap } from "react-leaflet"
import "leaflet-polylinedecorator"

import produce from "immer"
import { PolylineShapeOverwrite } from ".."
import { useCallbackSafeState } from "../../../hooks"
import type { IArrow, IArrowMapDrawing, IShape } from "../../../redux"
import type { PolylineOverwriteLayer } from "../../../utils"
import { ShapePopupPanel } from "../../molecules"

export interface IPolylineDecoratorProps {
  arrow: IArrowMapDrawing
}

const arrowHead: { [shapeId: string]: L.PolylineDecorator | undefined } = {}

export function PolylineDecorator(props: IPolylineDecoratorProps): ReactElement {
  const { arrow } = props

  const lineRef: RefObject<PolylineOverwriteLayer> = createRef<PolylineOverwriteLayer>()
  const [layer, setLayer] = useState<PolylineOverwriteLayer>()
  const [getShape, setShape] = useCallbackSafeState<IArrow>(arrow.shape)

  const map: LeafletMap = useMap()

  useEffect(() => {
    return () => {
      removeArrowHead()
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setShape(arrow.shape)
  }, [arrow.shape])

  useEffect(() => {
    if (!lineRef.current) return

    setLayer(lineRef.current)
  }, [lineRef])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    refreshArrowHead()
  }, [arrow, layer])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    refreshArrowHead()
  }, [arrow.shape.positions])

  function refreshArrowHead(): void {
    if (!layer) return
    const pattern: L.Pattern = getArrowPattern()

    removeArrowHead()
    const decorator: L.PolylineDecorator = L.polylineDecorator(layer, {
      patterns: [pattern]
    })

    decorator.addTo(map)
    arrowHead[arrow.id] = decorator
  }

  function removeArrowHead(): void {
    if (!arrowHead[arrow.id]) return
    arrowHead[arrow.id]!.remove()

    map.removeLayer(arrowHead[arrow.id]!)
  }

  function onShapeRemove(): void {
    removeArrowHead()
  }

  function getArrowPattern(): L.Pattern {
    return {
      offset: "100%",
      repeat: 0,
      symbol: L.Symbol.arrowHead({
        polygon: false,
        pixelSize: 5 * arrow.shape.lineWidth,
        pathOptions: {
          stroke: true,
          color: arrow.shape.color,
          weight: arrow.shape.lineWidth
        }
      })
    }
  }

  function duplicateShape(offsetFunction: (coord: LatLng) => LatLng): IShape {
    return produce(getShape(), (draft: IArrow) => {
      draft.positions = draft.positions.map((position: LatLng) => {
        return offsetFunction(position)
      })
    })
  }

  function calculatePopupPosition(): LatLngExpression {
    if (!layer) return [0, 0]

    const point1: Point = map.latLngToLayerPoint((layer as any)._latlngs[0])
    const point2: Point = map.latLngToLayerPoint((layer as any)._latlngs[1])

    const maxX: number = Math.max(point1.x, point2.x)
    const minX: number = Math.min(point1.x, point2.x)
    const minY: number = Math.min(point1.y, point2.y)
    const centerX: number = minX + (maxX - minX) / 2

    const coord: LatLng = map.layerPointToLatLng(new Point(centerX, minY))

    return [coord.lat, coord.lng]
  }

  return (
    <>
      <PolylineShapeOverwrite
        ref={lineRef}
        pathOptions={{
          weight: getShape().lineWidth,
          color: getShape().color
        }}
        latlngs={getShape().positions}
      >
        {layer && (
          <ShapePopupPanel
            shapeLayer={layer}
            drawing={arrow}
            onRemove={onShapeRemove}
            createDuplicate={duplicateShape}
            calculatePopupLocation={calculatePopupPosition}
          />
        )}
      </PolylineShapeOverwrite>
    </>
  )
}
